import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../auth/useAuth';

interface ChartPair {
  FundName: string;
  FieldName: string;
}

interface MultiChartExportDialogProps {
  open: boolean;
  onClose: () => void;
}

const MultiChartExportDialog: React.FC<MultiChartExportDialogProps> = ({ open, onClose }) => {
  const { getAccessToken } = useAuth();

  // Data state
  const [chartList, setChartList] = useState<ChartPair[]>([]);
  const [selections, setSelections] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [chartsPerPage, setChartsPerPage] = useState(12);
  const [cutoffDate, setCutoffDate] = useState<string>('');
  const [expandedFunds, setExpandedFunds] = useState<Set<string>>(new Set());

  // Helper to create a selection key
  const selKey = (fund: string, field: string) => `${fund}|||${field}`;
  const parseKey = (key: string): [string, string] => {
    const parts = key.split('|||');
    return [parts[0], parts[1]];
  };

  // Group chart list by fund
  const fundGroups = useMemo(() => {
    const groups: Record<string, string[]> = {};
    for (const item of chartList) {
      if (!groups[item.FundName]) groups[item.FundName] = [];
      groups[item.FundName].push(item.FieldName);
    }
    return groups;
  }, [chartList]);

  // Filtered funds based on search
  const filteredFunds = useMemo(() => {
    const query = searchQuery.toLowerCase();
    if (!query) return Object.keys(fundGroups).sort();

    return Object.keys(fundGroups)
      .filter(fund => {
        if (fund.toLowerCase().includes(query)) return true;
        return fundGroups[fund].some(field => field.toLowerCase().includes(query));
      })
      .sort();
  }, [fundGroups, searchQuery]);

  // Get filtered fields for a fund
  const getFilteredFields = (fund: string): string[] => {
    const query = searchQuery.toLowerCase();
    if (!query) return fundGroups[fund] || [];
    // If fund name matches, show all fields; otherwise filter fields
    if (fund.toLowerCase().includes(query)) return fundGroups[fund] || [];
    return (fundGroups[fund] || []).filter(f => f.toLowerCase().includes(query));
  };

  // Load data on open
  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token');

      // Fetch chart list and saved selections in parallel
      const [listResp, selResp] = await Promise.all([
        fetch(`${import.meta.env.VITE_API_BASE_URL}/api/hedgefund/chart-export-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${import.meta.env.VITE_API_BASE_URL}/api/hedgefund/chart-selections`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!listResp.ok) throw new Error('Failed to load chart list');
      const list: ChartPair[] = await listResp.json();
      setChartList(list);

      // Load saved selections
      if (selResp.ok) {
        const saved = await selResp.json();
        const newSelections = new Set<string>();
        for (const s of saved) {
          if (s.Selected) newSelections.add(selKey(s.FundName, s.FieldName));
        }
        setSelections(newSelections);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Toggle individual field
  const toggleField = (fund: string, field: string) => {
    const key = selKey(fund, field);
    setSelections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Toggle all fields for a fund
  const toggleFund = (fund: string) => {
    const fields = getFilteredFields(fund);
    const allSelected = fields.every(f => selections.has(selKey(fund, f)));
    setSelections(prev => {
      const next = new Set(prev);
      for (const f of fields) {
        if (allSelected) next.delete(selKey(fund, f));
        else next.add(selKey(fund, f));
      }
      return next;
    });
  };

  // Count selected for a fund
  const fundSelectedCount = (fund: string): number => {
    return (fundGroups[fund] || []).filter(f => selections.has(selKey(fund, f))).length;
  };

  // Save selections
  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token');

      const selArray = Array.from(selections).map(key => {
        const [fund, field] = parseKey(key);
        return { FundName: fund, FieldName: field, Selected: true };
      });

      const resp = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/hedgefund/chart-selections`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ selections: selArray })
      });

      if (!resp.ok) throw new Error('Failed to save selections');
      setSuccess(`Saved ${selArray.length} selections`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  // Export
  const handleExport = async (format: 'pdf' | 'excel') => {
    if (selections.size === 0) {
      setError('No charts selected for export');
      return;
    }

    setExporting(true);
    setError(null);
    setSuccess(null);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token');

      const selArray = Array.from(selections).map(key => {
        const [fund, field] = parseKey(key);
        return { FundName: fund, FieldName: field };
      });

      const endpoint = format === 'pdf' ? 'multi-chart-export/pdf' : 'multi-chart-export/excel';

      const resp = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/hedgefund/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ selections: selArray, charts_per_page: chartsPerPage, cutoff_date: cutoffDate || null })
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.detail || `Export failed`);
      }

      // Download the file
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Extract filename from Content-Disposition header or use default
      const disposition = resp.headers.get('Content-Disposition');
      const filenameMatch = disposition?.match(/filename=(.+)/);
      a.download = filenameMatch ? filenameMatch[1] : `Fund_Risk_Monitoring.${format === 'pdf' ? 'pdf' : 'xlsx'}`;

      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess(`${format.toUpperCase()} exported successfully with ${selections.size} charts`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const totalPairs = chartList.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6">Export Multiple Charts</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        {/* Controls bar */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search funds or fields..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 0.5 }} fontSize="small" /> }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Charts/Page</InputLabel>
            <Select
              value={chartsPerPage}
              label="Charts/Page"
              onChange={(e) => setChartsPerPage(Number(e.target.value))}
            >
              <MenuItem value={4}>4 per page</MenuItem>
              <MenuItem value={6}>6 per page</MenuItem>
              <MenuItem value={8}>8 per page</MenuItem>
              <MenuItem value={12}>12 per page</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title={cutoffDate ? `Showing data up to ${cutoffDate}` : 'Default: all dates'} arrow>
            <TextField
              size="small"
              type="month"
              label="Cutoff Date (optional)"
              value={cutoffDate}
              onChange={(e) => setCutoffDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
          </Tooltip>
          <Chip
            label={`${selections.size} of ${totalPairs} selected`}
            color={selections.size > 0 ? 'primary' : 'default'}
            size="small"
          />
        </Box>

        {/* Fund-Field selection list */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredFunds.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                {searchQuery ? 'No matching funds or fields' : 'No chart data available'}
              </Typography>
            ) : (
              filteredFunds.map(fund => {
                const fields = getFilteredFields(fund);
                const totalFields = (fundGroups[fund] || []).length;
                const selectedCount = fundSelectedCount(fund);
                const allSelected = fields.length > 0 && fields.every(f => selections.has(selKey(fund, f)));
                const someSelected = fields.some(f => selections.has(selKey(fund, f)));

                return (
                  <Accordion
                    key={fund}
                    expanded={expandedFunds.has(fund)}
                    onChange={() => {
                      setExpandedFunds(prev => {
                        const next = new Set(prev);
                        if (next.has(fund)) next.delete(fund);
                        else next.add(fund);
                        return next;
                      });
                    }}
                    disableGutters
                    sx={{ '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid', borderColor: 'divider', mb: 0.5 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center' } }}
                    >
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={(e) => { e.stopPropagation(); toggleFund(fund); }}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                        sx={{ p: 0.5, mr: 1 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'bold', flex: 1 }}>{fund}</Typography>
                      <Chip label={`${selectedCount}/${totalFields}`} size="small" sx={{ mr: 1 }}
                        color={selectedCount > 0 ? 'primary' : 'default'} variant="outlined" />
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                        {fields.map(field => (
                          <FormControlLabel
                            key={field}
                            control={
                              <Checkbox
                                checked={selections.has(selKey(fund, field))}
                                onChange={() => toggleField(fund, field)}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            }
                            label={<Typography variant="body2">{field}</Typography>}
                            sx={{ width: '50%', m: 0 }}
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
        <Button
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={loading || exporting}
          size="small"
        >
          Save Selections
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={16} /> : <PdfIcon />}
            onClick={() => handleExport('pdf')}
            disabled={loading || exporting || selections.size === 0}
            size="small"
          >
            Export PDF
          </Button>
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={16} /> : <ExcelIcon />}
            onClick={() => handleExport('excel')}
            disabled={loading || exporting || selections.size === 0}
            size="small"
            color="success"
          >
            Export Excel
          </Button>
          <Button onClick={onClose} size="small">Close</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default MultiChartExportDialog;
